import {
  Alert,
  Button,
  ButtonGroup,
  closeCurrentDialog,
  Form,
  FormButton,
  FormControl,
  FormField,
  FormSubmitHandler,
  Label,
  showNotification,
} from 'platform/components';
import {Box, HStack, Show, Text, VStack} from 'platform/foundation';
import {z} from 'zod';

import {UseFormReturn, useWatch} from 'react-hook-form';

import {head, isEmpty, isNotNil} from 'ramda';

import {BranchLeadAddressResponseBody, useCreateBranchLeadAddressMutation} from '@dms/api/sales';
import {isApiException} from '@dms/api/utils';
import i18n from '@dms/i18n';
import {handleApiError} from '@dms/shared';

import {
  ApiError,
  isFetchBaseQueryError,
  Nullish,
  RequiredTestIdProps,
  suffixTestId,
} from 'shared/web';

// The alias rule is still open in T20-142039. Keep it here only and in sync with the
// pattern, minLength and maxLength of the backend OpenAPI schema.
const ALIAS_PATTERN = /^[a-z][a-z0-9-]*$/;
const ALIAS_MIN_LENGTH = 2;
const ALIAS_MAX_LENGTH = 15;

const aliasRuleMessage = i18n.t('page.settings.labels.aiLeadManagementBranchLeadAliasRule', {
  min: ALIAS_MIN_LENGTH,
  max: ALIAS_MAX_LENGTH,
});

const requiredMessage = i18n.t('general.errors.mixed.required');

const createBranchLeadAddressFormSchema = z.object({
  // An empty Choice holds undefined before the first pick and null after a clear.
  branchId: z.string({required_error: requiredMessage, invalid_type_error: requiredMessage}),
  alias: z
    .string()
    // First, so an empty alias shows the required message and not the alias rule.
    .min(1, requiredMessage)
    .min(ALIAS_MIN_LENGTH, aliasRuleMessage)
    .max(ALIAS_MAX_LENGTH, aliasRuleMessage)
    .regex(ALIAS_PATTERN, aliasRuleMessage),
});

type CreateBranchLeadAddressFormType = z.infer<typeof createBranchLeadAddressFormSchema>;

// Backend domain errors that belong to the alias field: the alias breaks the domain rule
// (DMS-AL-LA-0004) or another branch already uses it (DMS-AL-LA-0005).
const ALIAS_ERROR_CODES = ['DMS-AL-LA-0004', 'DMS-AL-LA-0005'];

const getAliasErrorMessage = (error: ApiError) => {
  if (!isFetchBaseQueryError(error) || !isApiException(error.data)) {
    return null;
  }

  return (
    error.data.errors.find((apiError) => ALIAS_ERROR_CODES.includes(apiError.code))?.message ?? null
  );
};

interface CreateBranchLeadAddressFormProps extends RequiredTestIdProps {
  /** Active branches without an address, in API order. */
  branches: BranchLeadAddressResponseBody[];
  prefix: string;
  suffix: string;
  lengthLimit: number;
}

export function CreateBranchLeadAddressForm(props: CreateBranchLeadAddressFormProps) {
  const [createBranchLeadAddress] = useCreateBranchLeadAddressMutation();
  // With one branch left there is nothing to pick.
  const preselectedBranch = props.branches.length === 1 ? head(props.branches) : undefined;

  const handleSubmit: FormSubmitHandler<CreateBranchLeadAddressFormType> = async (
    values,
    setErrors
  ) => {
    await createBranchLeadAddress({branchId: values.branchId, alias: values.alias})
      .unwrap()
      .then(() => {
        showNotification.success(i18n.t('general.notifications.successfullyCreated'));
        closeCurrentDialog();
      })
      .catch((error: ApiError) => {
        const aliasErrorMessage = getAliasErrorMessage(error);

        if (isNotNil(aliasErrorMessage)) {
          setErrors([{name: 'alias', message: aliasErrorMessage}]);
          return;
        }

        handleApiError(error, {silentOnValidationError: true, setErrors});
      });
  };

  return (
    <Form<CreateBranchLeadAddressFormType>
      onSubmit={handleSubmit}
      experimentalZodSchema={createBranchLeadAddressFormSchema}
      defaultValues={{
        branchId: preselectedBranch?.branchId,
        alias: preselectedBranch?.suggestedAlias ?? '',
      }}
      data-testid={suffixTestId('createBranchLeadAddressForm', props)}
    >
      {(control, formApi) => (
        <VStack spacing={4}>
          <BranchField
            control={control}
            formApi={formApi}
            branches={props.branches}
            data-testid={suffixTestId('branch', props)}
          />

          <FormField
            control={control}
            name="alias"
            type="text"
            label={i18n.t('page.settings.labels.aiLeadManagementBranchLeadAlias')}
            helperText={i18n.t('page.settings.labels.aiLeadManagementBranchLeadAliasImmutable')}
            maxLength={ALIAS_MAX_LENGTH}
            isRequired
            data-testid={suffixTestId('alias', props)}
          />

          <EmailAddressPreview
            control={control}
            prefix={props.prefix}
            suffix={props.suffix}
            lengthLimit={props.lengthLimit}
            data-testid={suffixTestId('emailAddressPreview', props)}
          />

          <ButtonGroup align="right">
            <Button
              title={i18n.t('general.actions.cancel')}
              variant="secondary"
              onClick={closeCurrentDialog}
              data-testid={suffixTestId('cancel', props)}
            />
            <FormButton
              control={control}
              type="submit"
              title={i18n.t('general.actions.create')}
              data-testid={suffixTestId('create', props)}
            />
          </ButtonGroup>
        </VStack>
      )}
    </Form>
  );
}

interface BranchFieldProps extends RequiredTestIdProps {
  control: FormControl<CreateBranchLeadAddressFormType>;
  formApi: UseFormReturn<CreateBranchLeadAddressFormType>;
  branches: BranchLeadAddressResponseBody[];
}

function BranchField(props: BranchFieldProps) {
  // Still the previous branch inside the change handler: the form re-renders after it.
  const previousBranchId = useWatch({control: props.control, name: 'branchId'});

  const options = props.branches.map((branch) => ({
    label: branch.branchName,
    value: branch.branchId,
  }));

  const getSuggestedAlias = (branchId: string | number | Nullish) =>
    props.branches.find((branch) => branch.branchId === branchId)?.suggestedAlias ?? '';

  // A typed alias survives a change of branch: only an empty alias or the previous suggestion
  // is replaced by the suggestion for the new branch.
  const handleChange = (branchId: string | number | null) => {
    const alias = props.formApi.getValues('alias');

    if (!isEmpty(alias) && alias !== getSuggestedAlias(previousBranchId)) {
      return;
    }

    // Revalidate only an alias that shows an error, so a fresh dialog shows none before submit.
    props.formApi.setValue('alias', getSuggestedAlias(branchId), {
      shouldValidate: props.formApi.getFieldState('alias').invalid,
    });
  };

  return (
    <FormField
      control={props.control}
      name="branchId"
      type="choice"
      label={i18n.t('entity.branch.labels.branch')}
      placeholder={i18n.t(
        'page.settings.labels.aiLeadManagementBranchLeadAddressBranchPlaceholder'
      )}
      options={options}
      onChange={handleChange}
      isRequired
      isNotClearable
      menuInPortal
      data-testid={props['data-testid']}
    />
  );
}

interface EmailAddressPreviewProps extends RequiredTestIdProps {
  control: FormControl<CreateBranchLeadAddressFormType>;
  prefix: string;
  suffix: string;
  lengthLimit: number;
}

function EmailAddressPreview(props: EmailAddressPreviewProps) {
  const alias = useWatch({control: props.control, name: 'alias'});
  const emailAddress = `${props.prefix}${alias}${props.suffix}`;
  // A longer address still works, CarGate only cannot store it, so the dialog only warns.
  const isTooLong = emailAddress.length > props.lengthLimit;

  return (
    <VStack spacing={2}>
      <VStack spacing={1}>
        <Label>{i18n.t('page.settings.labels.aiLeadManagementBranchLeadAddress')}</Label>
        <Box
          paddingVertical={1}
          paddingHorizontal={2}
          borderRadius="small"
          border="1px solid"
          borderColor="general.separator"
        >
          <HStack spacing={2} justify="space-between" align="center">
            <Text size="small" overflowWrap="anywhere" data-testid={suffixTestId('value', props)}>
              {emailAddress}
            </Text>
            <Text
              size="xSmall"
              color={isTooLong ? 'warning' : 'secondary'}
              noWrap
              data-testid={suffixTestId('length', props)}
            >
              {i18n.t('page.settings.labels.aiLeadManagementBranchLeadAddressLength', {
                count: emailAddress.length,
                max: props.lengthLimit,
              })}
            </Text>
          </HStack>
        </Box>
      </VStack>

      <Show when={isTooLong}>
        <Alert
          variant="warning"
          title={i18n.t('page.settings.labels.aiLeadManagementBranchLeadAddressTooLong', {
            max: props.lengthLimit,
          })}
          data-testid={suffixTestId('tooLongWarning', props)}
        />
      </Show>
    </VStack>
  );
}
